import * as React from 'react';
import { useNavigate } from 'react-router';
import type { PiletApi } from '@chess/platform-web';
import { registerCard } from '@chess/api-dashboard';
import { CHESS_API, type Chess } from '@chess/api-chess';
import type { Fen, PieceType, Square, Uci } from '@chess/api-model';
import { registerAggregator, useAggregate } from '@chess/api-model/react';
import '@chess/api-insights/charts.css';
import { OpeningHabitsCard, TITLE, type OpeningHabitsCardProps } from './OpeningHabitsCard.tsx';
import { OpeningHabitsAggregator } from './OpeningHabitsAggregator.ts';

// Openings card "Positions you keep getting wrong" (2026-10-09): registers
// its own aggregator with the game store, reads its result and registers
// `OpeningHabitsCard` as a wide card body (the section draws its frame) on the Openings screen only, under the top cards that
// the Overview shares (`openings.history`, before the ranking tables). A row
// opens the newest of its games in the review at the position before the
// move (`#ply=N`), where the board draws the move played and the engine's.
// The engine's move is written as SAN through the `chess` API.
export function setup(app: PiletApi) {
  const getApi = <T,>(name: string) => app.getApi<T>(name);
  const aggregator = new OpeningHabitsAggregator();
  registerAggregator(getApi, aggregator);
  let chess: Chess | undefined;
  void getApi<Chess>(CHESS_API).then((c) => (chess = c));
  const sanOf = (fen: Fen, uci: Uci) => {
    const move = { from: uci.slice(0, 2) as Square, to: uci.slice(2, 4) as Square, promotion: (uci[4] as PieceType | undefined) || undefined };
    return chess?.play(fen, move)?.move.san;
  };
  const Card: React.FC = () => {
    const { result, state } = useAggregate(getApi, aggregator);
    const navigate = useNavigate();
    const review = React.useMemo<NonNullable<OpeningHabitsCardProps['review']>>(
      () => ({ href: (path, ply) => `${path}#ply=${ply}`, open: (path, ply) => navigate(`${path}#ply=${ply}`) }),
      [navigate],
    );
    // Never hidden while the store loads: the card shows skeletons for its values.
    if (state?.status !== 'ready') return state?.status === 'failed' ? null : <OpeningHabitsCard loading />;
    return result ? <OpeningHabitsCard result={result} review={review} sanOf={sanOf} /> : null;
  };
  registerCard(app, 'openings.history', Card, { order: 5, title: TITLE, wide: true });
}
